/**
 * Consultor digital — Fase 2 (70 rotinas restantes).
 */

import type {
  LeanFrequenciaLabel,
  LeanIelImpactLevel,
  LeanLibraryEnrichment,
  LeanNivel,
  LeanRoutineMaturity,
} from '../types/leanLibrary';

type Iel = {
  disciplina: LeanIelImpactLevel;
  aderenciaPadrao: LeanIelImpactLevel;
  resultado: LeanIelImpactLevel;
};

type Freq = Partial<Record<LeanNivel, LeanFrequenciaLabel>>;

function e(
  swiPassos: string[],
  itensConformidade: string[],
  indicadoresPrimarios: string[],
  indicadoresSecundarios: string[],
  relacaoCausal: string,
  frequenciaBenchmark: Freq,
  nivelMaturidade: LeanRoutineMaturity,
  ielImpacto: Iel,
): LeanLibraryEnrichment {
  return {
    swiPassos,
    itensConformidade,
    indicadoresPrimarios,
    indicadoresSecundarios,
    relacaoCausal,
    frequenciaBenchmark,
    nivelMaturidade,
    ielImpacto,
  };
}

const L1 = 'nivel-1-lider-producao' as LeanNivel;
const L2 = 'nivel-2-supervisor' as LeanNivel;
const L3 = 'nivel-3-coordenador' as LeanNivel;
const L4 = 'nivel-4-gerente' as LeanNivel;
const L5 = 'nivel-5-diretor' as LeanNivel;

export const LEAN_LIBRARY_ENRICHMENT_PHASE2: Record<string, LeanLibraryEnrichment> = {
  'n1-d-02': e(
    ['Conferir presença da equipe vs plano FTE do turno.', 'Identificar ausências, atrasos e realocações.', 'Cruzar capacidade disponível com plano hora a hora.', 'Acionar cobertura ou ajuste de ritmo se gap crítico.', 'Atualizar quadro FTE com gaps e ações.'],
    ['Presença conferida vs plano', 'Alocação por estação coerente com capacidade', 'Gaps documentados com plano de cobertura', 'Quadro FTE atualizado no turno'],
    ['Capacidade FTE vs plano', 'Gap de headcount no turno'],
    ['OEE', 'Horas perdidas por falta de pessoas'],
    'Capacidade real alinhada ao plano evita promessas impossíveis — protege cumprimento de meta e moral da equipe.',
    { [L1]: 'Diária', [L2]: 'Diária' },
    'basica',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n1-d-13': e(
    ['Selecionar operador e método a treinar (JI ou JM).', 'Preparar padrão de instrução (passos, pontos-chave, motivos).', 'Demonstrar método em 3 etapas TWI.', 'Operador executa sob supervisão com feedback.', 'Registrar treinamento e validar autonomia.'],
    ['Método demonstrado conforme padrão TWI', 'Operador executa sequência correta', 'Pontos críticos compreendidos', 'Registro de treinamento completo'],
    ['Operadores certificados no método', 'Tempo de ciclo pós-treinamento'],
    ['Defeitos por operação', 'Retrabalho'],
    'TWI padroniza transferência de conhecimento — reduz variabilidade e acelera curva de aprendizado.',
    { [L1]: 'Diária', [L2]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n1-d-15': e(
    ['Listar materiais, ferramentas e informações críticas do turno.', 'Verificar disponibilidade física e sistêmica (ERP/kanban).', 'Identificar restrições que bloqueiam produção.', 'Acionar PCP/logística/manutenção conforme criticidade.', 'Atualizar status até resolução ou contenção.'],
    ['Materiais críticos disponíveis', 'Ferramentas e dispositivos prontos', 'Informações de produção atualizadas', 'Restrições com dono e prazo'],
    ['Restrições abertas', 'Paradas por falta de material'],
    ['OEE', 'Cumprimento plano hora a hora'],
    'Antecipar restrições evita paradas evitáveis — protege fluxo e disponibilidade.',
    { [L1]: 'Diária', [L2]: 'Diária' },
    'basica',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n1-s-01': e(
    ['Abrir matriz de competências da equipe.', 'Identificar gaps por estação e nível de skill.', 'Cruzar gaps com plano de treinamento existente.', 'Priorizar desenvolvimento para postos críticos.', 'Atualizar matriz e comunicar plano à supervisão.'],
    ['Matriz reflete competências reais', 'Gaps críticos identificados', 'Plano de treinamento vinculado aos gaps', 'Postos críticos com cobertura mínima definida'],
    ['Gaps de competência', 'Cobertura de postos críticos'],
    ['Polivalência', 'Produtividade'],
    'Matriz atualizada direciona investimento em pessoas — reduz risco operacional por falta de skill.',
    { [L1]: 'Semanal', [L2]: 'Semanal', [L3]: 'Mensal' },
    'basica',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'medio' },
  ),

  'n1-s-03': e(
    ['Selecionar problema recorrente com dados (pareto, OEE, qualidade).', 'Montar equipe e definir problema de forma mensurável.', 'Conduzir análise 5W2H ou esboço A3.', 'Definir contramedidas com dono, prazo e indicador.', 'Registrar ata e agendar verificação de eficácia.'],
    ['Problema definido com métrica', 'Causa raiz preliminar documentada', 'Contramedidas SMART', 'Responsável e prazo definidos'],
    ['Problemas recorrentes', 'Eficácia de contramedidas'],
    ['Perdas associadas', 'Custo de não qualidade'],
    'Solução estruturada elimina firefighting — ataca causa raiz e evita recorrência.',
    { [L1]: 'Semanal', [L2]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n1-s-05': e(
    ['Coletar KPIs semanais da linha/célula.', 'Comparar real vs meta e vs semana anterior.', 'Identificar tendências (melhora/piora).', 'Documentar comentários de causa provável.', 'Definir foco da equipe para próxima semana.'],
    ['KPIs apresentados com fonte confiável', 'Tendência interpretada', 'Desvios com comentário de causa', 'Foco da próxima semana definido'],
    ['Atingimento meta semanal', 'Tendência de KPIs'],
    ['OEE', 'Scrap', 'Segurança'],
    'Revisão semanal converte dados em direção — alinha equipe antes que desvios se acumulem.',
    { [L1]: 'Semanal', [L2]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n1-s-07': e(
    ['Listar melhorias implementadas nas últimas semanas.', 'Visitar posto e verificar padrão novo documentado.', 'Observar execução vs padrão pós-melhoria.', 'Medir se ganho (tempo, qualidade) foi mantido.', 'Acionar re-sustentação se regressão detectada.'],
    ['Padrão pós-melhoria visível no posto', 'Execução conforme novo padrão', 'Indicador de ganho monitorado', 'Regressões tratadas'],
    ['Taxa de sustentação', 'Ganho mantido'],
    ['Retrabalho', 'Produtividade'],
    'Auditar sustentação garante que kaizen não reverta — protege ROI das melhorias.',
    { [L1]: 'Semanal', [L2]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n2-d-03': e(
    ['Abrir dashboard de KPIs operacionais do turno.', 'Validar integridade dos dados (fontes, atrasos).', 'Comparar real vs meta diária por linha.', 'Identificar desvios críticos e acionar líderes.', 'Registrar comentários e ações no painel.'],
    ['Dados de KPI confiáveis e atualizados', 'Desvios vs meta identificados', 'Ações acionadas para KPIs críticos', 'Painel comentado'],
    ['Atingimento meta diária', 'Desvios críticos'],
    ['OEE turno', 'Scrap'],
    'Monitoramento diário de KPIs permite correção de curso antes do fechamento do turno.',
    { [L2]: 'Diária', [L3]: 'Diária' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-d-04': e(
    ['Receber escalonamentos da camada 1 não resolvidos.', 'Classificar criticidade (segurança, qualidade, entrega).', 'Alocar recursos ou decisão imediata.', 'Comunicar retorno ao líder com prazo.', 'Registrar escalonamento e status até fechamento.'],
    ['Escalonamentos classificados por criticidade', 'Decisão ou recurso alocado', 'Retorno comunicado à camada 1', 'Registro completo até fechamento'],
    ['Tempo de resposta escalonamento', 'Escalonamentos abertos'],
    ['OEE', 'Anomalias críticas'],
    'Escalonamento eficaz na camada 2 evita que problemas críticos permaneçam sem dono.',
    { [L2]: 'Diária', [L3]: 'Diária' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-d-06': e(
    ['Listar recursos críticos (pessoas-chave, máquinas, materiais).', 'Verificar status de disponibilidade em tempo real.', 'Identificar riscos de indisponibilidade no turno.', 'Acionar plano de contingência se necessário.', 'Documentar status e decisões.'],
    ['Recursos críticos mapeados', 'Disponibilidade verificada', 'Riscos com plano de contingência', 'Status documentado'],
    ['Disponibilidade recursos críticos', 'Paradas evitadas'],
    ['OEE', 'Capacidade FTE'],
    'Proteger recursos críticos sustenta throughput — evita gargalos por indisponibilidade evitável.',
    { [L2]: 'Diária', [L3]: 'Diária' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-d-07': e(
    ['Coletar barreiras reportadas pelos líderes.', 'Priorizar por impacto em fluxo e meta.', 'Atuar diretamente ou escalar para áreas de apoio.', 'Acompanhar até remoção ou contenção.', 'Atualizar lista com status transparente.'],
    ['Barreiras priorizadas por impacto', 'Ação ou escalonamento definido', 'Status atualizado até resolução', 'Líderes informados do progresso'],
    ['Barreiras removidas', 'Tempo médio de remoção'],
    ['OEE', 'Lead time operacional'],
    'Remoção ágil de barreiras acelera fluxo — destrava produção sem esperar reuniões formais.',
    { [L2]: 'Diária', [L3]: 'Diária' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-s-01': e(
    ['Revisar plano de produção vs capacidade real da área.', 'Identificar sobrecarga, ociosidade ou sequenciamento inviável.', 'Negociar ajustes com PCP (prioridades, volumes, datas).', 'Comunicar plano acordado aos líderes.', 'Registrar ata com compromissos mútuos.'],
    ['Plano cruzado com capacidade real', 'Sobrecargas identificadas e tratadas', 'Ajustes acordados documentados', 'Líderes alinhados ao plano revisado'],
    ['Aderência ao plano', 'Horas de ociosidade/sobrecarga'],
    ['OEE', 'Entregas no prazo'],
    'Alinhamento com PCP evita plano irreal — reduz firefighting e melhora previsibilidade.',
    { [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-s-05': e(
    ['Preparar pauta individual (desempenho, TPL, desenvolvimento).', 'Conduzir one-on-one em ambiente reservado.', 'Ouvir feedback do líder e alinhar expectativas.', 'Definir 1–2 compromissos mútuos com prazo.', 'Registrar notas e revisar na próxima sessão.'],
    ['Pauta estruturada conduzida', 'Compromissos SMART definidos', 'Feedback bidirecional registrado', 'Follow-up da sessão anterior'],
    ['Desenvolvimento de liderança', 'Compromissos cumpridos'],
    ['IEL por líder', 'Aderência TPL'],
    'Coaching individual eleva maturidade da camada 1 — cascateia disciplina e conformidade na operação.',
    { [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-s-08': e(
    ['Consolidar planos de ação de todas as linhas da supervisão.', 'Revisar status, atrasos e evidências.', 'Repriorizar ações críticas cross-linha.', 'Escalar bloqueios estruturais à coordenação.', 'Atualizar painel consolidado.'],
    ['Status consolidado por ação', 'Atrasos com novo prazo ou escalonamento', 'Evidências de conclusão verificadas', 'Painel confiável'],
    ['% conclusão planos supervisão', 'Ações atrasadas'],
    ['Recorrência problemas', 'OEE área'],
    'Consolidar PDCA na supervisão evita ações órfãs — fecha ciclo de melhoria em escala.',
    { [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-s-09': e(
    ['Consolidar KPIs semanais de todas as linhas.', 'Analisar tendências e outliers.', 'Preparar relatório semanal com comentários.', 'Definir foco tático para próxima semana.', 'Comunicar em fórum de performance.'],
    ['KPIs consolidados corretamente', 'Tendências interpretadas', 'Relatório com causas comentadas', 'Foco próxima semana definido'],
    ['Atingimento meta semanal área', 'Tendência KPIs'],
    ['OEE', 'Scrap', 'Segurança'],
    'Análise semanal consolidada direciona recursos da supervisão para onde o impacto é maior.',
    { [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-m-01': e(
    ['Coletar fechamento de todos os KPIs mensais da área.', 'Validar dados com financeiro/controlling se aplicável.', 'Comparar real vs meta mensal e vs mês anterior.', 'Documentar causas de desvio significativo.', 'Assinar e comunicar relatório mensal.'],
    ['KPIs fechados com dados auditáveis', 'Comparação vs meta documentada', 'Causas de desvio explicadas', 'Relatório assinado e distribuído'],
    ['Atingimento meta mensal', 'Variação vs mês anterior'],
    ['OEE mensal', 'Custo operacional'],
    'Fechamento mensal formaliza accountability — base para decisões de investimento e foco.',
    { [L2]: 'Mensal', [L3]: 'Mensal', [L4]: 'Mensal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n2-m-02': e(
    ['Revisar desempenho de cada líder (TPL, KPIs, liderança).', 'Preparar feedback estruturado com exemplos.', 'Conduzir conversa de avaliação individual.', 'Definir plano de desenvolvimento e metas.', 'Registrar ficha de avaliação.'],
    ['Critérios de avaliação aplicados consistentemente', 'Feedback documentado com exemplos', 'Plano de desenvolvimento definido', 'Metas acordadas'],
    ['Desempenho liderança', 'Plano desenvolvimento ativo'],
    ['IEL líderes', 'Rotatividade equipe'],
    'Avaliação estruturada desenvolve pipeline de liderança — eleva efetividade da camada 1.',
    { [L2]: 'Mensal', [L3]: 'Mensal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'baixo', resultado: 'alto' },
  ),

  'n2-m-03': e(
    ['Mapear gaps de treinamento da equipe (matriz vs necessidade).', 'Priorizar treinamentos por risco operacional.', 'Alinhar com RH/T&D calendário e recursos.', 'Definir plano mensal de capacitação.', 'Comunicar cronograma aos líderes.'],
    ['Gaps mapeados por estação', 'Priorização por risco documentada', 'Plano mensal com datas e responsáveis', 'Recursos alinhados com RH'],
    ['Horas de treinamento', 'Gaps fechados'],
    ['Polivalência', 'Defeitos por operação'],
    'Treinamento planejado fecha gaps de competência — reduz risco e variabilidade operacional.',
    { [L2]: 'Mensal', [L3]: 'Mensal' },
    'basica',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'medio' },
  ),

  'n2-m-04': e(
    ['Coletar demanda prevista e capacidade instalada.', 'Modelar cenários (turno, headcount, OEE).', 'Identificar gaps de capacidade de médio prazo.', 'Propor ajustes (turno, investimento, terceiros).', 'Documentar estudo e recomendações.'],
    ['Demanda e capacidade quantificadas', 'Cenários modelados', 'Gaps identificados com prazo', 'Recomendações documentadas'],
    ['Capacidade vs demanda', 'Utilização capacidade'],
    ['OEE', 'Lead time'],
    'Estudo de capacidade antecipa gargalos estruturais — evita surpresas no cumprimento de pedidos.',
    { [L2]: 'Mensal', [L3]: 'Mensal', [L4]: 'Mensal' },
    'avancada',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-m-05': e(
    ['Conferir headcount real vs orçamento aprovado.', 'Explicar variações (contratações, turnover, temporários).', 'Avaliar adequação ao volume de produção.', 'Identificar oportunidades de eficiência.', 'Atualizar quadro e comunicar RH/financeiro.'],
    ['Headcount conferido vs orçamento', 'Variações explicadas', 'Adequação ao volume avaliada', 'Quadro atualizado'],
    ['Headcount vs orçamento', 'Produtividade por FTE'],
    ['Custo mão de obra', 'OEE'],
    'Headcount alinhado à demanda equilibra custo e capacidade — evita ociosidade ou sobrecarga crônica.',
    { [L2]: 'Mensal', [L3]: 'Mensal', [L4]: 'Mensal' },
    'intermediaria',
    { disciplina: 'baixo', aderenciaPadrao: 'baixo', resultado: 'alto' },
  ),

  'n3-d-01': e(
    ['Definir roteiro transversal (múltiplas células/áreas).', 'Observar padrão, cultura e indicadores visuais.', 'Validar consistência entre supervisões.', 'Registrar achados sistêmicos vs locais.', 'Distribuir ações e revisar na próxima caminhada.'],
    ['Padrão observado vs documentado', 'Consistência entre áreas avaliada', 'Achados sistêmicos identificados', 'Ações com dono definidas'],
    ['Desvios sistêmicos', 'Ações pós-gemba'],
    ['OEE coordenação', 'Nota 5S média'],
    'Gemba transversal detecta variação entre áreas — direciona padronização e cultura.',
    { [L3]: 'Diária', [L4]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n3-d-02': e(
    ['Abrir painel de KPIs críticos da coordenação.', 'Validar dados e comparar vs meta diária.', 'Identificar áreas/supervisões em risco.', 'Acionar supervisores para plano de recuperação.', 'Registrar comentários no painel crítico.'],
    ['KPIs críticos atualizados', 'Riscos de meta identificados', 'Planos de recuperação acionados', 'Painel comentado'],
    ['Atingimento meta diária consolidada', 'KPIs críticos em alerta'],
    ['OEE coordenação', 'Entregas'],
    'Monitoramento diário de KPIs críticos permite intervenção antes do fechamento do dia.',
    { [L3]: 'Diária', [L4]: 'Diária' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n3-d-03': e(
    ['Receber barreiras escaladas pelos supervisores.', 'Classificar impacto cross-área.', 'Mobilizar áreas de apoio ou decisão gerencial.', 'Acompanhar até remoção.', 'Registrar log de barreiras removidas.'],
    ['Barreiras classificadas', 'Recursos ou decisão alocados', 'Status transparente até fechamento', 'Log atualizado'],
    ['Barreiras removidas', 'Tempo de resolução cross-área'],
    ['OEE', 'Lead time'],
    'Coordenação destrava barreiras entre áreas — acelera fluxo sistêmico.',
    { [L3]: 'Diária', [L4]: 'Diária' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n3-d-04': e(
    ['Revisar lista de problemas crônicos ativos.', 'Verificar status de contramedidas e indicadores.', 'Identificar normalização ou estagnação.', 'Escalar se sem progresso por 2+ ciclos.', 'Atualizar painel de crônicos.'],
    ['Lista de crônicos atualizada', 'Contramedidas com progresso mensurável', 'Estagnação identificada e escalada', 'Indicadores monitorados'],
    ['Problemas crônicos abertos', 'Tempo médio resolução crônicos'],
    ['Perdas associadas', 'OEE'],
    'Monitorar crônicos evita aceitar falhas como normais — mantém pressão por solução estrutural.',
    { [L3]: 'Diária', [L4]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n3-d-05': e(
    ['Receber problema complexo multi-área escalado.', 'Montar equipe cross-funcional.', 'Facilitar sessão estruturada (A3, ishikawa, 8D).', 'Definir contramedidas sistêmicas com donos.', 'Registrar ata e cronograma de verificação.'],
    ['Equipe cross-funcional formada', 'Análise estruturada conduzida', 'Contramedidas sistêmicas definidas', 'Cronograma de verificação acordado'],
    ['Problemas complexos fechados', 'Eficácia contramedidas'],
    ['Perdas sistêmicas', 'Recorrência'],
    'Facilitação de problemas complexos resolve causas que nenhuma área resolve sozinha.',
    { [L3]: 'Diária', [L4]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n3-s-01': e(
    ['Consolidar performance semanal das áreas.', 'Conduzir fórum com supervisores.', 'Comparar áreas e identificar best practices.', 'Definir foco tático da coordenação.', 'Registrar ata de performance.'],
    ['KPIs consolidados apresentados', 'Comparação entre áreas objetiva', 'Foco tático definido', 'Ata registrada'],
    ['Atingimento meta semanal', 'Gap entre áreas'],
    ['OEE coordenação', 'Scrap'],
    'Fórum de performance alinha supervisões — evita silos e otimização local.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n3-s-02': e(
    ['Consolidar KPIs semanais da coordenação.', 'Analisar tendências e desvios vs meta.', 'Preparar relatório com comentários de causa.', 'Definir ações corretivas prioritárias.', 'Comunicar em fórum de performance.'],
    ['Relatório KPI completo', 'Tendências documentadas', 'Ações corretivas priorizadas', 'Comunicação às áreas'],
    ['Atingimento meta semanal', 'Tendência KPIs'],
    ['OEE', 'Custo qualidade'],
    'Revisão semanal de KPIs na coordenação direciona recursos para desvios significativos.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n3-s-03': e(
    ['Abrir painel de projetos lean em andamento.', 'Revisar status, marcos e riscos de cada projeto.', 'Identificar projetos atrasados ou sem sponsor.', 'Remover barreiras cross-área dos projetos críticos.', 'Atualizar painel e prioridades.'],
    ['Status de projetos atualizado', 'Marcos críticos monitorados', 'Barreiras endereçadas', 'Priorização revisada'],
    ['Projetos no prazo', 'Ganho previsto vs realizado'],
    ['ROI projetos lean', 'OEE'],
    'Gestão de portfólio lean garante avanço de transformação estrutural — não só kaizen pontual.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n3-s-06': e(
    ['Revisar relatórios 8D abertos da coordenação.', 'Validar rigor de cada etapa (D1–D8).', 'Confirmar eficácia de ações corretivas.', 'Escalar 8D críticos ou atrasados.', 'Atualizar painel 8D.'],
    ['Etapas 8D completas conforme método', 'Ações corretivas com evidência', 'Eficácia verificada antes de fechar', 'Prazos monitorados'],
    ['8D abertos', 'Tempo médio fechamento 8D'],
    ['Recorrência defeitos', 'Custo qualidade'],
    '8D rigoroso fecha loop com cliente e interno — evita repetição de escapes críticos.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'muito_alto' },
  ),

  'n3-s-07': e(
    ['Preparar pauta de coaching por supervisor.', 'Conduzir sessão focada em maturidade TPL e KPIs.', 'Identificar gaps de liderança e plano de ação.', 'Definir compromissos mútuos.', 'Registrar notas e follow-up.'],
    ['Sessão estruturada conduzida', 'Gaps de liderança identificados', 'Compromissos SMART definidos', 'Follow-up anterior revisado'],
    ['Maturidade supervisores', 'Compromissos cumpridos'],
    ['IEL camada 2', 'OEE área'],
    'Coaching de supervisores eleva camada 2 — multiplica efetividade em toda a coordenação.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n3-s-08': e(
    ['Consolidar planos de ação da coordenação.', 'Revisar status, atrasos e eficácia.', 'Repriorizar ações estratégico-táticas.', 'Escalar bloqueios à gerência.', 'Atualizar painel consolidado.'],
    ['Status consolidado confiável', 'Atrasos tratados ou escalados', 'Eficácia verificada em ações fechadas', 'Painel atualizado'],
    ['% conclusão planos coordenação', 'Ações atrasadas'],
    ['Recorrência problemas', 'OEE'],
    'PDCA na coordenação garante execução de compromissos cross-área.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n3-s-09': e(
    ['Mapear fluxo e identificar gargalos atuais.', 'Quantificar impacto de cada gargalo no throughput.', 'Priorizar ataque ao gargalo sistêmico.', 'Definir ações com dono e indicador.', 'Atualizar mapa de gargalos.'],
    ['Gargalo principal identificado', 'Impacto quantificado', 'Ações com dono e meta', 'Mapa atualizado'],
    ['Throughput', 'WIP no gargalo'],
    ['Lead time', 'OEE'],
    'Atacar o gargalo sistêmico maximiza ganho de fluxo — princípio TOC aplicado à operação.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n3-s-10': e(
    ['Comparar capacidade semanal vs plano de produção.', 'Identificar áreas sob/sobre carregadas.', 'Propor realocação entre áreas ou turnos.', 'Alinhar com PCP e supervisores.', 'Documentar estudo semanal.'],
    ['Capacidade quantificada por área', 'Desbalanceamento identificado', 'Realocação acordada', 'Estudo documentado'],
    ['Utilização capacidade', 'Gap capacidade vs plano'],
    ['OEE', 'Entregas'],
    'Balanceamento semanal de capacidade evita gargalos artificiais entre áreas.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'intermediaria',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n3-s-11': e(
    ['Preparar escalonamentos e KPIs consolidados.', 'Apresentar TOP 3 obstáculos estruturais ao gerente.', 'Revisar decisões anteriores da camada 4.', 'Obter direcionamento ou recursos.', 'Registrar ata e desdobrar.'],
    ['Obstáculos documentados', 'Decisões gerenciais registradas', 'Recursos acordados', 'Desdobramento comunicado'],
    ['Escalonamentos resolvidos', 'Tempo resposta gerência'],
    ['Investimentos', 'OEE unidade'],
    'Camada 4 destrava decisões de investimento e política — fora do alcance da coordenação.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'avancada',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n3-m-01': e(
    ['Consolidar headcount de todas as áreas.', 'Comparar vs orçamento e vs mês anterior.', 'Explicar variações e impacto no custo.', 'Propor ajustes se desalinhamento estrutural.', 'Publicar relatório mensal.'],
    ['Headcount consolidado auditável', 'Variações explicadas', 'Impacto custo quantificado', 'Relatório publicado'],
    ['Headcount vs orçamento', 'Custo pessoal'],
    ['Produtividade FTE', 'OEE'],
    'Headcount consolidado conecta operação ao P&L — base para decisões de estrutura.',
    { [L3]: 'Mensal', [L4]: 'Mensal' },
    'intermediaria',
    { disciplina: 'baixo', aderenciaPadrao: 'baixo', resultado: 'alto' },
  ),

  'n3-m-02': e(
    ['Coletar custos variáveis e fixos da coordenação.', 'Analisar desvios vs orçamento.', 'Identificar drivers de custo (scrap, hora extra, manutenção).', 'Propor ações de redução priorizadas.', 'Documentar relatório de custos.'],
    ['Custos categorizados corretamente', 'Desvios vs orçamento explicados', 'Drivers identificados', 'Ações priorizadas'],
    ['Custo operacional', 'Desvio vs orçamento'],
    ['Scrap', 'Horas extras', 'OEE'],
    'Visibilidade de custos direciona kaizen financeiro — conecta operação à margem.',
    { [L3]: 'Mensal', [L4]: 'Mensal' },
    'avancada',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n3-m-03': e(
    ['Definir escopo da auditoria sistêmica (SGQ/SGA integrado).', 'Amostrar execução de rotinas de gestão diária.', 'Verificar registros, indicadores e escalonamentos.', 'Classificar NCs sistêmicas vs locais.', 'Emitir relatório com plano corretivo.'],
    ['Rotinas de gestão executadas conforme sistema', 'Registros completos e rastreáveis', 'Indicadores confiáveis', 'NCs classificadas com plano'],
    ['Score auditoria sistêmica', 'NCs sistêmicas abertas'],
    ['Maturidade gestão', 'Recorrência NCs'],
    'Auditoria sistêmica valida se o sistema de gestão funciona — não só conformidade documental.',
    { [L3]: 'Mensal', [L4]: 'Mensal' },
    'avancada',
    { disciplina: 'alto', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n3-m-04': e(
    ['Revisar direcionadores estratégicos desdobrados para a área.', 'Comparar progresso vs metas trimestrais/anuais.', 'Identificar gaps e realinhar iniciativas.', 'Validar conexão operação-estratégia com supervisores.', 'Registrar ata de revisão estratégica.'],
    ['Direcionadores revisados', 'Progresso vs meta documentado', 'Iniciativas realinhadas', 'Supervisores alinhados'],
    ['Atingimento metas estratégicas área', 'Iniciativas no prazo'],
    ['OEE', 'Produtividade'],
    'Revisão estratégica evita operação desconectada dos objetivos corporativos.',
    { [L3]: 'Mensal', [L4]: 'Mensal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n3-m-05': e(
    ['Coletar dados de produtividade mensal (output/input).', 'Comparar vs meta, benchmark e mês anterior.', 'Decompor variação (volume, eficiência, qualidade).', 'Identificar áreas de ganho/perda.', 'Publicar relatório de produtividade.'],
    ['Produtividade calculada consistentemente', 'Variação decomposta', 'Causas documentadas', 'Relatório publicado'],
    ['Produtividade', 'Eficiência operacional'],
    ['OEE', 'Custo unitário'],
    'Produtividade mensal conecta esforço operacional a resultado — base para metas e investimentos.',
    { [L3]: 'Mensal', [L4]: 'Mensal' },
    'intermediaria',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n3-m-06': e(
    ['Revisar pipeline de investimentos operacionais (CAPEX/OPEX).', 'Priorizar por ROI e alinhamento estratégico.', 'Validar status de investimentos em andamento.', 'Escalar aprovações pendentes à gerência.', 'Atualizar lista priorizada.'],
    ['Pipeline completo e atualizado', 'Priorização por critério objetivo', 'Status de investimentos monitorado', 'Aprovações escaladas quando necessário'],
    ['ROI investimentos', 'Investimentos no prazo'],
    ['OEE pós-investimento', 'Capacidade'],
    'Pipeline de investimentos prioriza capital onde o retorno operacional é maior.',
    { [L3]: 'Mensal', [L4]: 'Mensal', [L5]: 'Mensal' },
    'avancada',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n4-d-01': e(
    ['Selecionar áreas críticas ou com desvio de meta.', 'Conduzir gemba gerencial com coordenadores.', 'Validar presença de padrão, cultura e liderança.', 'Registrar achados e expectativas claras.', 'Acompanhar ações na próxima visita.'],
    ['Áreas críticas visitadas', 'Padrão e cultura observados', 'Achados registrados', 'Expectativas comunicadas'],
    ['Ações pós-gemba gerencial', 'Desvios corrigidos'],
    ['OEE unidade', 'IEL médio'],
    'Presença gerencial no gemba reforça prioridades — alinha cultura e accountability.',
    { [L4]: 'Diária', [L5]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n4-d-02': e(
    ['Abrir painel de KPIs críticos da unidade/planta.', 'Validar integridade e comparar vs meta corporativa.', 'Identificar riscos de não atingimento.', 'Acionar coordenações para plano de recuperação.', 'Registrar comentários executivos.'],
    ['KPIs críticos atualizados', 'Risco meta corporativa avaliado', 'Planos de recuperação acionados', 'Painel comentado'],
    ['Atingimento meta unidade', 'KPIs em alerta'],
    ['OEE planta', 'Margem'],
    'KPIs críticos diários permitem intervenção gerencial antes do fechamento mensal.',
    { [L4]: 'Diária', [L5]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n4-d-03': e(
    ['Receber barreiras escaladas pela coordenação.', 'Avaliar impacto estratégico e cross-funcional.', 'Tomar decisão ou mobilizar diretoria/áreas staff.', 'Comunicar retorno com prazo claro.', 'Registrar log de barreiras estratégicas.'],
    ['Barreiras classificadas estrategicamente', 'Decisão ou recurso alocado', 'Retorno comunicado', 'Log atualizado'],
    ['Barreiras estratégicas removidas', 'Tempo de decisão'],
    ['OEE', 'Projetos estratégicos'],
    'Gerência destrava decisões cross-funcionais — acelera transformação e fluxo.',
    { [L4]: 'Diária', [L5]: 'Semanal' },
    'avancada',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n4-s-02': e(
    ['Consolidar KPIs estratégicos da unidade.', 'Comparar vs metas do Hoshin/plano anual.', 'Analisar tendências e projeção de fechamento.', 'Definir ações corretivas gerenciais.', 'Comunicar em fórum de coordenadores.'],
    ['KPIs estratégicos consolidados', 'Progresso vs Hoshin documentado', 'Projeção de fechamento', 'Ações gerenciais definidas'],
    ['Atingimento metas estratégicas', 'Tendência KPIs'],
    ['OEE planta', 'Resultado financeiro'],
    'KPIs estratégicos conectam operação diária às metas anuais — evita surpresa no fechamento.',
    { [L4]: 'Semanal', [L5]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n4-s-03': e(
    ['Revisar portfólio de projetos estratégicos.', 'Validar marcos, orçamento e sponsors.', 'Identificar projetos em risco.', 'Remover barreiras de projetos prioritários.', 'Atualizar painel executivo.'],
    ['Status projetos atualizado', 'Marcos críticos monitorados', 'Riscos identificados', 'Barreiras endereçadas'],
    ['Projetos estratégicos no prazo', 'ROI previsto'],
    ['OEE', 'Capacidade'],
    'Gestão de projetos estratégicos garante entrega de iniciativas de transformação.',
    { [L4]: 'Semanal', [L5]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n4-s-04': e(
    ['Selecionar coordenadores/supervisores-chave para coaching.', 'Preparar pauta (KPIs, liderança, pipeline).', 'Conduzir sessão de desenvolvimento.', 'Definir compromissos e plano de sucessão.', 'Registrar notas gerenciais.'],
    ['Sessão estruturada', 'Compromissos definidos', 'Plano sucessão revisado', 'Notas registradas'],
    ['Maturidade liderança', 'Pipeline sucessão'],
    ['IEL unidade', 'Turnover liderança'],
    'Coaching gerencial fortalece bench de liderança — garante continuidade e performance.',
    { [L4]: 'Semanal', [L5]: 'Mensal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'baixo', resultado: 'alto' },
  ),

  'n4-s-05': e(
    ['Revisar painel de problemas crônicos da unidade.', 'Avaliar progresso e eficácia de contramedidas.', 'Escalar crônicos estagnados à diretoria.', 'Realocar recursos se necessário.', 'Atualizar painel e prioridades.'],
    ['Crônicos com status atualizado', 'Contramedidas monitoradas', 'Estagnação escalada', 'Recursos realocados se aplicável'],
    ['Problemas crônicos abertos', 'Tempo resolução'],
    ['Perdas crônicas', 'OEE'],
    'Gestão gerencial de crônicos impede normalização de falhas sistêmicas na unidade.',
    { [L4]: 'Semanal', [L5]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n4-s-06': e(
    ['Consolidar performance operacional da planta.', 'Conduzir fórum gerencial com coordenadores.', 'Comparar áreas e direcionar foco.', 'Definir metas táticas da semana.', 'Registrar ata de performance.'],
    ['Performance consolidada apresentada', 'Comparação entre coordenações', 'Metas táticas definidas', 'Ata registrada'],
    ['Atingimento meta semanal planta', 'Gap entre coordenações'],
    ['OEE', 'Segurança', 'Qualidade'],
    'Performance consolidada alinha toda a planta — evita otimização local prejudicial.',
    { [L4]: 'Semanal', [L5]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n4-s-07': e(
    ['Revisar planos de ação estratégicos gerenciais.', 'Validar status, prazos e eficácia.', 'Repriorizar conforme mudança estratégica.', 'Escalar bloqueios à diretoria.', 'Atualizar painel estratégico.'],
    ['Planos estratégicos atualizados', 'Eficácia verificada', 'Priorização revisada', 'Bloqueios escalados'],
    ['% conclusão planos estratégicos', 'Ações atrasadas'],
    ['Metas Hoshin', 'ROI iniciativas'],
    'PDCA gerencial fecha compromissos estratégicos — conecta decisão à execução.',
    { [L4]: 'Semanal', [L5]: 'Semanal' },
    'avancada',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n4-s-08': e(
    ['Preparar escalonamentos e riscos consolidados da unidade.', 'Apresentar à diretoria TOP 3 riscos/decisões.', 'Revisar retorno de escalonamentos anteriores.', 'Obter direcionamento executivo.', 'Registrar ata camada 5.'],
    ['Riscos documentados', 'Decisões diretoria registradas', 'Retorno comunicado', 'Ata completa'],
    ['Escalonamentos resolvidos', 'Tempo resposta diretoria'],
    ['Metas corporativas', 'Investimentos'],
    'Camada 5 alinha unidade à estratégia corporativa — destrava decisões de capital e política.',
    { [L4]: 'Semanal', [L5]: 'Semanal' },
    'excelencia',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n4-m-01': e(
    ['Consolidar resultado financeiro da unidade (P&L operacional).', 'Analisar desvios vs orçamento.', 'Conectar drivers operacionais ao financeiro.', 'Identificar oportunidades de margem.', 'Apresentar relatório mensal.'],
    ['P&L consolidado', 'Desvios explicados', 'Drivers operacionais vinculados', 'Relatório apresentado'],
    ['Margem operacional', 'Desvio vs orçamento'],
    ['OEE', 'Custo unitário', 'Scrap'],
    'Revisão financeira conecta gemba ao P&L — torna visível impacto das perdas operacionais.',
    { [L4]: 'Mensal', [L5]: 'Mensal' },
    'avancada',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n4-m-02': e(
    ['Coletar produtividade consolidada da planta.', 'Comparar vs benchmark interno/externo.', 'Decompor gaps por área e driver.', 'Definir plano de recuperação gerencial.', 'Publicar relatório.'],
    ['Produtividade calculada', 'Benchmark aplicado', 'Gaps decompostos', 'Plano definido'],
    ['Produtividade planta', 'Gap vs benchmark'],
    ['OEE', 'Custo unitário'],
    'Produtividade vs benchmark revela potencial oculto — direciona transformação lean.',
    { [L4]: 'Mensal', [L5]: 'Mensal' },
    'intermediaria',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n4-m-03': e(
    ['Consolidar headcount da unidade vs orçamento.', 'Analisar estrutura vs demanda de médio prazo.', 'Identificar oportunidades de reestruturação.', 'Alinhar com RH e diretoria.', 'Documentar quadro e recomendações.'],
    ['Headcount auditável', 'Estrutura vs demanda avaliada', 'Recomendações documentadas', 'Alinhamento RH/diretoria'],
    ['Headcount vs orçamento', 'Produtividade FTE'],
    ['Custo pessoal', 'OEE'],
    'Headcount gerencial equilibra eficiência e capacidade — impacto direto na margem.',
    { [L4]: 'Mensal', [L5]: 'Mensal' },
    'intermediaria',
    { disciplina: 'baixo', aderenciaPadrao: 'baixo', resultado: 'alto' },
  ),

  'n4-m-04': e(
    ['Aplicar framework de maturidade lean (pilares, critérios).', 'Auditar amostra de áreas e rotinas TPL.', 'Calcular score por pilar e consolidado.', 'Identificar gaps vs target de maturidade.', 'Definir roadmap de transformação.'],
    ['Framework aplicado consistentemente', 'Score por pilar calculado', 'Gaps identificados', 'Roadmap definido'],
    ['Score maturidade lean', 'Gap vs target'],
    ['OEE', 'IEL médio'],
    'Auditoria de maturidade lean mede progresso real da transformação — não só iniciativas isoladas.',
    { [L4]: 'Mensal', [L5]: 'Mensal' },
    'excelencia',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'muito_alto' },
  ),

  'n4-m-05': e(
    ['Revisar roadmap de melhorias de médio prazo.', 'Validar alinhamento com estratégia e capacidade.', 'Repriorizar iniciativas por impacto.', 'Atualizar cronograma e recursos.', 'Comunicar às coordenações.'],
    ['Roadmap atualizado', 'Priorização por impacto', 'Recursos alocados', 'Comunicação às áreas'],
    ['Iniciativas no prazo', 'Ganho acumulado roadmap'],
    ['OEE', 'ROI melhorias'],
    'Roadmap de melhorias organiza transformação — evita dispersão de esforços kaizen.',
    { [L4]: 'Mensal', [L5]: 'Mensal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n4-m-06': e(
    ['Revisar pipeline de talentos e sucessão da unidade.', 'Avaliar readiness de sucessores por posição-chave.', 'Identificar gaps de desenvolvimento.', 'Atualizar plano de sucessão.', 'Alinhar com RH e diretoria.'],
    ['Pipeline atualizado', 'Readiness avaliado', 'Gaps de desenvolvimento identificados', 'Plano sucessão revisado'],
    ['Cobertura sucessão posições-chave', 'Readiness sucessores'],
    ['Turnover liderança', 'IEL'],
    'Pipeline de talentos garante continuidade — reduz risco operacional por vacância de liderança.',
    { [L4]: 'Mensal', [L5]: 'Mensal' },
    'intermediaria',
    { disciplina: 'baixo', aderenciaPadrao: 'baixo', resultado: 'alto' },
  ),

  'n4-m-07': e(
    ['Modelar capacidade vs demanda de médio prazo (6–18 meses).', 'Identificar necessidade de expansão/retração.', 'Avaliar investimentos em capacidade.', 'Propor plano à diretoria.', 'Documentar estudo.'],
    ['Demanda projetada documentada', 'Capacidade modelada', 'Gap identificado', 'Plano proposto'],
    ['Capacidade vs demanda futura', 'Utilização projetada'],
    ['Investimentos CAPEX', 'Lead time'],
    'Planejamento de capacidade evita crises de supply — antecipa investimentos ou ajustes.',
    { [L4]: 'Mensal', [L5]: 'Mensal' },
    'avancada',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n5-s-01': e(
    ['Selecionar planta/área para visita executiva.', 'Conduzir gemba com presença visível da diretoria.', 'Dialogar com operadores e líderes no posto.', 'Registrar compromissos e expectativas.', 'Acompanhar ações na próxima visita.'],
    ['Visita executiva realizada', 'Diálogo no gemba documentado', 'Compromissos registrados', 'Follow-up agendado'],
    ['Ações pós-gemba executivo', 'Engajamento operação'],
    ['IEL planta', 'Cultura lean'],
    'Presença executiva no gemba reforça cultura e prioridades — sinaliza compromisso da diretoria.',
    { [L5]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n5-s-02': e(
    ['Consolidar performance operacional corporativa.', 'Conduzir fórum com gerentes/diretores de unidade.', 'Comparar unidades e identificar gaps.', 'Definir direcionadores corporativos da semana.', 'Registrar ata de performance corporativa.'],
    ['Performance consolidada apresentada', 'Comparação entre unidades', 'Direcionadores definidos', 'Ata registrada'],
    ['Atingimento meta corporativa', 'Gap entre unidades'],
    ['OEE corporativo', 'Margem'],
    'Performance corporativa alinha todas as unidades — evita divergência estratégica.',
    { [L5]: 'Semanal' },
    'avancada',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-s-03': e(
    ['Revisar KPIs estratégicos vinculados ao Hoshin.', 'Comparar progresso vs metas anuais.', 'Identificar unidades/initiativas em risco.', 'Direcionar recursos corporativos.', 'Atualizar painel estratégico.'],
    ['KPIs Hoshin atualizados', 'Progresso vs meta documentado', 'Riscos identificados', 'Recursos direcionados'],
    ['Atingimento metas Hoshin', 'Breakthrough objectives'],
    ['OEE corporativo', 'Resultado financeiro'],
    'KPIs estratégicos monitoram desdobramento do Hoshin — garante foco corporativo.',
    { [L5]: 'Semanal' },
    'excelencia',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-s-04': e(
    ['Revisar portfólio de projetos prioritários da diretoria.', 'Validar sponsors, orçamento e marcos.', 'Identificar projetos em risco crítico.', 'Mobilizar recursos corporativos.', 'Atualizar painel prioritário.'],
    ['Portfólio atualizado', 'Marcos críticos monitorados', 'Riscos endereçados', 'Recursos mobilizados'],
    ['Projetos prioritários no prazo', 'ROI corporativo'],
    ['Transformação lean', 'Capacidade'],
    'Projetos prioritários concentram capital e atenção — garantem entrega de breakthroughs.',
    { [L5]: 'Semanal' },
    'excelencia',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-s-05': e(
    ['Revisar registro de riscos operacionais e de continuidade.', 'Avaliar probabilidade x impacto.', 'Priorizar TOP riscos da semana.', 'Definir planos de mitigação ou contingência.', 'Comunicar à presidência se crítico.'],
    ['Riscos catalogados e classificados', 'TOP riscos priorizados', 'Planos mitigação definidos', 'Comunicação executiva se aplicável'],
    ['Riscos operacionais abertos', 'Incidentes evitados'],
    ['Acidentes', 'Paradas não programadas'],
    'Gestão proativa de riscos evita crises — protege continuidade operacional e reputação.',
    { [L5]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-s-06': e(
    ['Preparar escalonamentos e riscos corporativos.', 'Apresentar à presidência/board decisões pendentes.', 'Revisar retorno de escalonamentos anteriores.', 'Obter direcionamento e registrar compromissos.', 'Desdobrar para unidades.'],
    ['Escalonamentos documentados', 'Decisões board registradas', 'Compromissos desdobrados', 'Retorno comunicado'],
    ['Decisões implementadas', 'Tempo resposta board'],
    ['Metas corporativas', 'Investimentos'],
    'Camada com presidência alinha operação ao board — destrava decisões de mais alto nível.',
    { [L5]: 'Semanal' },
    'excelencia',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-m-02': e(
    ['Consolidar progresso vs metas estratégicas anuais.', 'Analisar projeção de fechamento do ano.', 'Identificar metas em risco.', 'Definir planos de recuperação corporativos.', 'Comunicar às unidades.'],
    ['Progresso vs meta documentado', 'Projeção de fechamento', 'Metas em risco identificadas', 'Planos de recuperação definidos'],
    ['Atingimento metas anuais', 'Gap vs projeção'],
    ['OEE corporativo', 'EBITDA'],
    'Revisão de metas estratégicas mantém pressão no fechamento anual — evita surpresa no Q4.',
    { [L5]: 'Mensal' },
    'excelencia',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-m-03': e(
    ['Revisar pipeline de sucessão executiva.', 'Avaliar readiness para posições C-level/chave.', 'Identificar gaps de talento crítico.', 'Atualizar plano de sucessão corporativo.', 'Alinhar com conselho/RH.'],
    ['Pipeline executivo atualizado', 'Readiness avaliado', 'Gaps identificados', 'Plano alinhado com conselho'],
    ['Cobertura sucessão executiva', 'Readiness C-level'],
    ['Turnover executivo', 'Engajamento liderança'],
    'Sucessão executiva garante continuidade estratégica — reduz risco de vacância em posições críticas.',
    { [L5]: 'Mensal' },
    'excelencia',
    { disciplina: 'baixo', aderenciaPadrao: 'baixo', resultado: 'alto' },
  ),

  'n5-m-04': e(
    ['Revisar portfólio de investimentos corporativos.', 'Priorizar por ROI estratégico e risco.', 'Validar status de investimentos em curso.', 'Aprovar/repriorizar alocação de capital.', 'Comunicar decisões às unidades.'],
    ['Portfólio completo', 'Priorização por ROI estratégico', 'Status monitorado', 'Decisões comunicadas'],
    ['ROI investimentos', 'Investimentos no prazo'],
    ['Capacidade corporativa', 'Margem'],
    'Alocação de capital prioriza transformação — conecta investimento a breakthrough estratégico.',
    { [L5]: 'Mensal' },
    'excelencia',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-m-05': e(
    ['Definir escopo auditoria lean corporativa.', 'Avaliar maturidade por unidade e pilar.', 'Comparar unidades e identificar best practices.', 'Consolidar score sistêmico.', 'Definir roadmap corporativo de lean.'],
    ['Framework aplicado em todas unidades', 'Score consolidado', 'Best practices identificadas', 'Roadmap corporativo definido'],
    ['Score maturidade lean corporativo', 'Gap vs target'],
    ['OEE corporativo', 'IEL médio'],
    'Auditoria lean corporativa mede maturidade sistêmica — direciona transformação em escala.',
    { [L5]: 'Mensal' },
    'excelencia',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'muito_alto' },
  ),

  'n5-m-06': e(
    ['Consolidar resultados financeiros corporativos.', 'Analisar desvios vs orçamento e vs ano anterior.', 'Conectar drivers operacionais ao resultado.', 'Identificar oportunidades de margem corporativa.', 'Apresentar à presidência/conselho.'],
    ['Resultados consolidados auditáveis', 'Desvios explicados', 'Drivers operacionais vinculados', 'Apresentação executiva'],
    ['EBITDA/margem operacional', 'Desvio vs orçamento'],
    ['OEE corporativo', 'Custo não qualidade'],
    'Resultado financeiro consolidado valida se estratégia e operação geram valor — fecha loop Hoshin.',
    { [L5]: 'Mensal' },
    'excelencia',
    { disciplina: 'baixo', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-m-07': e(
    ['Revisar portfólio completo de projetos corporativos.', 'Balancear pipeline (transformação, manutenção, growth).', 'Identificar sobrecarga ou gaps no portfólio.', 'Repriorizar conforme estratégia.', 'Atualizar governance de portfólio.'],
    ['Portfólio balanceado', 'Priorização alinhada à estratégia', 'Governance atualizada', 'Sobrecarga identificada'],
    ['Projetos no prazo', 'Valor do portfólio'],
    ['ROI transformação', 'Capacidade'],
    'Governança de portfólio evita dispersão — concentra recursos nos projetos de maior impacto.',
    { [L5]: 'Mensal' },
    'excelencia',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-m-08': e(
    ['Avaliar maturidade do sistema de gestão corporativo (ISO, lean, digital).', 'Auditar consistência entre unidades.', 'Identificar gaps de governança e compliance.', 'Definir plano de elevação de maturidade.', 'Reportar ao conselho.'],
    ['Maturidade avaliada por dimensão', 'Consistência entre unidades verificada', 'Gaps de governança identificados', 'Plano de elevação definido'],
    ['Score maturidade sistema', 'NCs sistêmicas'],
    ['Compliance', 'Auditorias externas'],
    'Maturidade do sistema de gestão eleva governança — reduz risco regulatório e operacional.',
    { [L5]: 'Mensal' },
    'excelencia',
    { disciplina: 'alto', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),
};
